import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Chart as ChartJS, ScriptableContext, TooltipModel } from "chart.js";
import React from "react";

import { positionTooltip } from "@/common/components/chart/chart.utils";
import { numberFormatter } from "@/common/services/formatter";

import { PredictionRate } from "./yield-predictions.api";

type PredictionChartProps = {
  data: PredictionRate[];
};

const PredictionChart = ({ data }: PredictionChartProps) => {
  const { _ } = useLingui();
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const lineRef = React.useRef<HTMLDivElement>(null);

  const labels = React.useMemo(
    () => ({
      max: _(t`Horní hranice spolehlivosti predikce výnosu (90%)`),
      min: _(t`Dolní hranice spolehlivosti predikce výnosu (90%)`),
      prediction: _(t`Odhad výnosů`),
    }),
    [_],
  );

  React.useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const existingChart = ChartJS.getChart(chartRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    const chart = new ChartJS(chartRef.current, {
      data: {
        datasets: [
          // Upper bound (dashed line)
          {
            backgroundColor: "transparent",
            borderColor: getLineColor,
            borderDash: [4, 4],
            borderWidth: 1,
            data: data,
            fill: false,
            parsing: {
              xAxisKey: "date",
              yAxisKey: "upper",
            },
            pointRadius: 0,
          },
          // Lower bound (dashed line)
          {
            backgroundColor: "transparent",
            borderColor: getLineColor,
            borderDash: [4, 4],
            borderWidth: 1,
            data: data,
            fill: false,
            parsing: {
              xAxisKey: "date",
              yAxisKey: "lower",
            },
            pointRadius: 0,
          },
          // Prediction line
          {
            backgroundColor: "transparent",
            borderColor: getLineColor,
            borderWidth: 3,
            data: data,
            fill: false,
            parsing: {
              xAxisKey: "date",
              yAxisKey: "prediction",
            },
            pointRadius: 0,
          },
        ],
      },
      options: {
        animation: false,
        interaction: {
          axis: "x",
          mode: "nearest",
        },
        layout: {
          padding: 0,
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: ({ dataIndex }) => {
                const item = data[dataIndex];
                return numberFormatter({
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).format(item.prediction);
              },
              title: () => "",
            },
            displayColors: false,
            enabled: false,
            external: (args) =>
              externalTooltipHandler(
                args,
                tooltipRef.current!,
                lineRef.current!,
                data,
                labels,
              ),
            filter: (item) => item.datasetIndex === 2,
            intersect: false,
            position: "nearest",
          },
        },
        responsive: true,
        scales: {
          x: {
            border: {
              color: "#caccd1",
            },
            grid: {
              color: "transparent",
            },
            ticks: {
              align: "inner",
              autoSkip: true,
            },
          },
          y: {
            beginAtZero: false,
            border: {
              color: "transparent",
              dash: [2, 2],
            },
            grid: {
              tickColor: (context) =>
                context.index === 0 ? "#caccd1" : "transparent",
              tickLength: 10,
            },
            ticks: {
              callback: (value) => {
                if (value === 0) {
                  return "";
                }
                return numberFormatter({
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }).format(
                  typeof value === "number" ? value : parseFloat(value),
                );
              },
            },
            title: {
              display: true,
              text: "t/ha",
            },
          },
        },
      },
      type: "line",
    });

    return () => {
      chart.destroy();
    };
  }, [data, labels]);

  return (
    <div className="relative h-[200px] sm:h-[280px]">
      <div ref={lineRef} />
      <canvas className="mr-4 max-h-[280px]" ref={chartRef} />
      <div
        className="pointer-events-none absolute whitespace-nowrap rounded-lg bg-[#4D5570CC] text-xs text-white"
        data-testid="chart-tooltip"
        ref={tooltipRef}
      />
    </div>
  );
};

const getLineColor = (context: ScriptableContext<"line">) => {
  const { ctx, chartArea } = context.chart;

  if (!chartArea) {
    return;
  }

  const gradient = ctx.createLinearGradient(
    chartArea.left,
    0,
    chartArea.right,
    0,
  );
  gradient.addColorStop(0, "#60a6d3");
  gradient.addColorStop(1, "#168b6b");
  return gradient;
};

type TooltipLabels = {
  max: string;
  min: string;
  prediction: string;
};

const externalTooltipHandler = (
  context: { chart: ChartJS; tooltip: TooltipModel<"line"> },
  tooltipEl: HTMLDivElement,
  lineEl: HTMLDivElement,
  data: PredictionRate[],
  labels: TooltipLabels,
) => {
  const { chart, tooltip } = context;

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    lineEl.style.opacity = "0";
    return;
  }

  const dataIndex = tooltip.dataPoints[0]?.dataIndex;
  const item = data[dataIndex];
  const format = (value: number) =>
    numberFormatter({
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);

  // eslint-disable-next-line lingui/no-unlocalized-strings
  tooltipEl.innerHTML = `
      <div class="font-bold mb-1">${item.date}</div>
      <div class="flex flex-col gap-1">
        <div class="flex justify-between gap-4"><span>${labels.max}:</span><span class="text-right">${format(item.upper)} t/ha</span></div>
        <div class="flex justify-between gap-4"><span>${labels.prediction}:</span><span class="text-right font-bold">${format(item.prediction)} t/ha</span></div>
        <div class="flex justify-between gap-4"><span>${labels.min}:</span><span class="text-right">${format(item.lower)} t/ha</span></div>
      </div>
    `;

  positionTooltip({ chart, lineEl, tooltip, tooltipEl });
};

export { PredictionChart };
